import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import SportsGolfIcon from "@mui/icons-material/SportsGolf";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Link as RouterLink } from "react-router-dom";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";

type MetricType =
  | "LOGIN_SUCCESS"
  | "ROBOT_CLEANER_MENU_CLICK"
  | "SCREEN_GOLF_MENU_CLICK"
  | "SMALL_LIBRARY_MENU_CLICK"
  | "BOOK_LOAN_STATUS_VIEW"
  | "BOOK_CAFE_MENU_CLICK"
  | "BOOK_CAFE_GUIDE_CLICK"
  | "BOOK_CAFE_GUIDE_DETAIL_CLICK"
  | "BOOK_CAFE_BOARD_GAME_LIST_CLICK";

interface PropsType {
  userState: userState;
}

interface MetricDefinition {
  type: MetricType;
  label: string;
  description: string;
  color: string;
  icon: JSX.Element;
  fallbackKeys: string[];
}

type SummaryCounts = Record<MetricType, number>;
type DailyCounts = Record<MetricType, Record<string, number>>;

interface DashboardData {
  counts: SummaryCounts;
  dailyCounts: DailyCounts;
  dateKeys: string[];
}

const mainApi = new HttpMainApi();

const METRICS: MetricDefinition[] = [
  {
    type: "LOGIN_SUCCESS",
    label: "로그인 성공 수",
    description: "기간 내 로그인 성공 이벤트",
    color: "#2e3341",
    icon: <LoginIcon />,
    fallbackKeys: ["loginSuccessCount", "login_success", "LOGIN_SUCCESS_COUNT"],
  },
  {
    type: "ROBOT_CLEANER_MENU_CLICK",
    label: "청소 로봇 메뉴 클릭수",
    description: "창문 로봇청소기 예약 메뉴 진입",
    color: "#1f6f8b",
    icon: <CleaningServicesIcon />,
    fallbackKeys: ["robotCleanerMenuClickCount", "robotCleanerCount", "ROBOT_CLEANER_MENU_CLICK_COUNT"],
  },
  {
    type: "SCREEN_GOLF_MENU_CLICK",
    label: "골프예약 메뉴 클릭수",
    description: "스크린 골프 예약 메뉴 진입",
    color: "#7a5c1e",
    icon: <SportsGolfIcon />,
    fallbackKeys: ["screenGolfMenuClickCount", "screenGolfCount", "SCREEN_GOLF_MENU_CLICK_COUNT"],
  },
  {
    type: "SMALL_LIBRARY_MENU_CLICK",
    label: "작은도서관 클릭수",
    description: "작은도서관 메뉴/배너 진입",
    color: "#356b3f",
    icon: <MenuBookIcon />,
    fallbackKeys: ["smallLibraryMenuClickCount", "smallLibraryCount", "SMALL_LIBRARY_MENU_CLICK_COUNT"],
  },
  {
    type: "BOOK_LOAN_STATUS_VIEW",
    label: "도서 대여 현황 조회수",
    description: "도서 대여 현황 페이지 진입",
    color: "#5f6f62",
    icon: <MenuBookIcon />,
    fallbackKeys: ["bookLoanStatusViewCount", "bookLoanStatusCount", "BOOK_LOAN_STATUS_VIEW_COUNT"],
  },
  {
    type: "BOOK_CAFE_MENU_CLICK",
    label: "북카페 예약 클릭수",
    description: "북카페 예약 메뉴 및 예약 버튼 진입",
    color: "#8a4b2a",
    icon: <LocalCafeIcon />,
    fallbackKeys: ["bookCafeMenuClickCount", "bookCafeCount", "BOOK_CAFE_MENU_CLICK_COUNT"],
  },
  {
    type: "BOOK_CAFE_GUIDE_CLICK",
    label: "북카페 안내 클릭수",
    description: "북카페 이용 안내 메뉴 진입",
    color: "#9b5f3f",
    icon: <LocalCafeIcon />,
    fallbackKeys: ["bookCafeGuideClickCount", "BOOK_CAFE_GUIDE_CLICK_COUNT"],
  },
  {
    type: "BOOK_CAFE_GUIDE_DETAIL_CLICK",
    label: "북카페 상세 가이드 클릭수",
    description: "북카페 공간별 상세 가이드 진입",
    color: "#a86f49",
    icon: <LocalCafeIcon />,
    fallbackKeys: ["bookCafeGuideDetailClickCount", "BOOK_CAFE_GUIDE_DETAIL_CLICK_COUNT"],
  },
  {
    type: "BOOK_CAFE_BOARD_GAME_LIST_CLICK",
    label: "보드게임 리스트 클릭수",
    description: "북카페 보드게임 리스트 열기",
    color: "#6e503d",
    icon: <LocalCafeIcon />,
    fallbackKeys: ["bookCafeBoardGameListClickCount", "BOOK_CAFE_BOARD_GAME_LIST_CLICK_COUNT"],
  },
];

function formatDateInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultDateRange() {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - 6);
  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(today),
  };
}

function emptyCounts(): SummaryCounts {
  return {
    LOGIN_SUCCESS: 0,
    ROBOT_CLEANER_MENU_CLICK: 0,
    SCREEN_GOLF_MENU_CLICK: 0,
    SMALL_LIBRARY_MENU_CLICK: 0,
    BOOK_LOAN_STATUS_VIEW: 0,
    BOOK_CAFE_MENU_CLICK: 0,
    BOOK_CAFE_GUIDE_CLICK: 0,
    BOOK_CAFE_GUIDE_DETAIL_CLICK: 0,
    BOOK_CAFE_BOARD_GAME_LIST_CLICK: 0,
  };
}

function emptyDailyCounts(): DailyCounts {
  return {
    LOGIN_SUCCESS: {},
    ROBOT_CLEANER_MENU_CLICK: {},
    SCREEN_GOLF_MENU_CLICK: {},
    SMALL_LIBRARY_MENU_CLICK: {},
    BOOK_LOAN_STATUS_VIEW: {},
    BOOK_CAFE_MENU_CLICK: {},
    BOOK_CAFE_GUIDE_CLICK: {},
    BOOK_CAFE_GUIDE_DETAIL_CLICK: {},
    BOOK_CAFE_BOARD_GAME_LIST_CLICK: {},
  };
}

function toNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeDateKey(value: unknown) {
  if (value === undefined || value === null || value === "") return "";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length < 8) return "";
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
}

function parseDateKey(dateKey: string) {
  const normalized = normalizeDateKey(dateKey);
  if (!normalized) return undefined;
  const [year, month, day] = normalized.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function getDateKeys(startDate: string, endDate: string) {
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);
  if (!start || !end || start > end) return [normalizeDateKey(startDate)].filter(Boolean);

  const keys: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    keys.push(formatDateInput(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

function formatChartDate(dateKey: string) {
  const normalized = normalizeDateKey(dateKey);
  if (!normalized) return String(dateKey);
  return `${normalized.slice(5, 7)}/${normalized.slice(8, 10)}`;
}

function getResultPayload(response: any) {
  return response?.response?.result ?? response?.response ?? response ?? {};
}

function getRowMetricType(row: any) {
  return row?.metricType ?? row?.eventType ?? row?.type ?? row?.metric ?? row?.name;
}

function getRowCount(row: any) {
  return row?.count ?? row?.total ?? row?.value ?? row?.cnt ?? 0;
}

function getRowDate(row: any) {
  return row?.eventDate ?? row?.date ?? row?.dt ?? row?.day ?? row?.inputDt;
}

function normalizeDashboardData(response: any, startDate: string, endDate: string): DashboardData {
  const payload = getResultPayload(response);
  const counts = emptyCounts();
  const dailyCounts = emptyDailyCounts();
  const dateKeys = getDateKeys(startDate, endDate);
  const listPayload = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.metrics)
      ? payload.metrics
      : Array.isArray(payload?.summary)
        ? payload.summary
        : undefined;

  if (listPayload) {
    listPayload.forEach((row: any) => {
      const metricType = getRowMetricType(row);
      if (metricType in counts) {
        const typedMetric = metricType as MetricType;
        const count = toNumber(getRowCount(row));
        counts[typedMetric] += count;

        const dateKey = normalizeDateKey(getRowDate(row));
        if (dateKey) {
          dailyCounts[typedMetric][dateKey] = (dailyCounts[typedMetric][dateKey] ?? 0) + count;
        }
      }
    });
    return { counts, dailyCounts, dateKeys };
  }

  const objectPayload =
    payload?.metrics && !Array.isArray(payload.metrics)
      ? payload.metrics
      : payload?.summary && !Array.isArray(payload.summary)
        ? payload.summary
        : payload;

  METRICS.forEach((metric) => {
    const directValue = objectPayload?.[metric.type];
    const fallbackValue = metric.fallbackKeys.map((key) => objectPayload?.[key]).find((value) => value !== undefined);
    const count = toNumber(directValue ?? fallbackValue);
    counts[metric.type] = count;
    const lastDate = dateKeys[dateKeys.length - 1];
    if (lastDate && count > 0) dailyCounts[metric.type][lastDate] = count;
  });

  return { counts, dailyCounts, dateKeys };
}

function buildChartOptions(metric: MetricDefinition, data: DashboardData): Highcharts.Options {
  const seriesData = data.dateKeys.map((dateKey) => data.dailyCounts[metric.type][dateKey] ?? 0);
  return {
    chart: {
      type: "column",
      height: 176,
      backgroundColor: "transparent",
      spacing: [8, 4, 4, 4],
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories: data.dateKeys.map(formatChartDate),
      lineColor: "#e4e7ec",
      tickColor: "#e4e7ec",
      labels: {
        style: { color: "#667085", fontSize: "10px" },
      },
    },
    yAxis: {
      min: 0,
      allowDecimals: false,
      title: { text: undefined },
      gridLineColor: "#eef1f5",
      labels: {
        style: { color: "#667085", fontSize: "10px" },
      },
    },
    tooltip: {
      outside: true,
      pointFormat: "<b>{point.y}</b>건",
    },
    plotOptions: {
      column: {
        borderRadius: 4,
        color: metric.color,
        pointPadding: 0.16,
        groupPadding: 0.08,
      },
      series: {
        animation: false,
      },
    },
    series: [
      {
        type: "column",
        name: metric.label,
        data: seriesData,
      },
    ],
  };
}

const AdminStatsDashboard = (props: PropsType) => {
  const defaultRange = useMemo(getDefaultDateRange, []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    counts: emptyCounts(),
    dailyCounts: emptyDailyCounts(),
    dateKeys: getDateKeys(defaultRange.startDate, defaultRange.endDate),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const getDetailPath = (metricType: MetricType) =>
    `/adminstats/${metricType}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;

  const fetchSummary = useCallback(async (queryStartDate: string, queryEndDate: string) => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const res = await mainApi.get_dashboard_daily_stats({
        fromDate: queryStartDate,
        toDate: queryEndDate,
        eventType: "ALL",
        userId: props.userState.userId,
      });

      if (res.code === "200") {
        setDashboardData(normalizeDashboardData(res, queryStartDate, queryEndDate));
      } else {
        setDashboardData({
          counts: emptyCounts(),
          dailyCounts: emptyDailyCounts(),
          dateKeys: getDateKeys(queryStartDate, queryEndDate),
        });
        setErrorMessage(res.response?.error_msg ?? "대시보드 지표를 조회하지 못했습니다.");
      }
    } catch (error) {
      setDashboardData({
        counts: emptyCounts(),
        dailyCounts: emptyDailyCounts(),
        dateKeys: getDateKeys(queryStartDate, queryEndDate),
      });
      setErrorMessage("대시보드 지표를 조회하지 못했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [props.userState.userId]);

  useEffect(() => {
    fetchSummary(defaultRange.startDate, defaultRange.endDate);
  }, [defaultRange.endDate, defaultRange.startDate, fetchSummary]);

  return (
    <Stack
      spacing={3}
      sx={{
        minHeight: "100%",
        boxSizing: "border-box",
        p: { xs: 2, md: 3 },
        backgroundColor: "#f6f7fb",
      }}
    >
      <Box>
        <Typography variant="h5" fontWeight={800} color="#202633">
          커뮤니티 대시보드
        </Typography>
        <Typography variant="body2" color="#667085" sx={{ mt: 0.75 }}>
          최근 7일 기준 로그인과 주요 메뉴 진입 일별 지표를 확인합니다.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          p: 2,
          border: "1px solid #dfe4ec",
          borderRadius: "8px",
          backgroundColor: "#fff",
        }}
      >
        <TextField
          label="시작일"
          type="date"
          size="small"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="종료일"
          type="date"
          size="small"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          onClick={() => fetchSummary(startDate, endDate)}
          sx={{ height: 40, borderRadius: "4px", boxShadow: "none" }}
        >
          조회
        </Button>
      </Box>

      {isLoading && <LinearProgress />}
      {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

      <Grid container spacing={2}>
        {METRICS.map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.type}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: "8px",
                border: "1px solid #dfe4ec",
                boxShadow: "0 8px 22px rgba(32, 38, 51, 0.06)",
              }}
            >
              <CardActionArea component={RouterLink} to={getDetailPath(metric.type)} sx={{ height: "100%" }}>
                <CardContent sx={{ height: "100%", boxSizing: "border-box" }}>
                  <Stack spacing={2} sx={{ height: "100%" }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        backgroundColor: metric.color,
                      }}
                    >
                      {metric.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="#667085" fontWeight={700}>
                        {metric.label}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.75 }}>
                        <Typography variant="h5" fontWeight={900} color="#202633">
                          {dashboardData.counts[metric.type].toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="#667085" fontWeight={700}>
                          최근 기간 합계
                        </Typography>
                      </Stack>
                      <Box sx={{ mt: 1.5 }}>
                        <HighchartsReact highcharts={Highcharts} options={buildChartOptions(metric, dashboardData)} />
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" color={metric.color}>
                      <Typography variant="body2" fontWeight={800}>
                        상세 보기
                      </Typography>
                      <ArrowForwardIosIcon sx={{ width: 14, height: 14 }} />
                    </Stack>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default AdminStatsDashboard;
